import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Logo, colors } from "brand";
import {
  useActiveOrg,
  PinLock,
  GateHeader,
  FullScreenLoader,
  isShowInActiveWindow,
} from "common-fe";
import { useTranslation } from "i18n-fe";
import { useShows } from "../hooks/useShows";
import { useScribeAssignments, soleShow } from "../hooks/useScribeAssignments";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useRidesByClassId } from "../hooks/useRidesByClassId";
import {
  classNumberLabel,
  formatVenueTime,
  nextAssignedClass,
  nextUpcomingRide,
} from "../util/classDisplay";
import { rideCnoNumber } from "../util/rideDisplay";
import { scribePositionsFor } from "../util/scribeAssignment";
import { useScoreContext } from "../context/ScoreContext";
import { RESUME_ROUTE_KEY } from "../hooks/useInactivityRedirect";
import { getTimeFormat, setTimeFormat, HOUR_12, HOUR_24 } from "../store/timeFormat";
import { fullSignOut } from "../auth/signOut";
import { getScribePin } from "../util/scribePin";
import ScribeResume from "./ScribeResume";
import RebuildLocalDataButton from "./RebuildLocalDataButton";

// #2078 — GateHeader's `brandLogo` slot is empty by default (a gate is a
// dead end, not a place to navigate from — see GateHeader.jsx's own doc
// comment), so this lock screen never passed one, which also collapsed the
// rightContent account block to the left edge (AppBar.jsx lays the two out
// with `justify-content: space-between`; with only one child present it
// sits at flex-start instead of flex-end). Deliberately NOT TopBar's
// `BrandWordmark` (that one navigates to /classes on click) — this is a
// static mark, matching the "dead end" doctrine the gate already documents.
function GateBrandMark() {
  const { t } = useTranslation("common");
  return (
    <Box data-testid="gate-brand-logo" sx={{ display: "flex", alignItems: "center" }}>
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

// #710 — 12hr vs 24hr is a per-scribe display preference (store/timeFormat.js),
// default 12hr. Drives BOTH the wall clock and the scheduled-time labels below
// it, so the whole standby screen agrees on one format.
//
// #1558 — this clock is deliberately left BROWSER-local, unlike
// formatScheduledTime below it. `d` here is always `new Date()` (see
// WallClock's `useState(() => new Date())`/`setInterval` below) — a live
// "what time is it right now, on THIS device" instant, not a venue
// scheduledTime read back off FVD. That's the same distinction announcer-fe
// draws between its own formatTime.js (forces the venue zone for FVD
// scheduledTime) and formatClock.js's formatClockTime (deliberately
// browser-local for "this happened just now" instants) — a scribe checking
// this screen from off-site should see THEIR OWN wall clock here, not the
// venue's.
function formatClock(d, format) {
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  if (format === HOUR_24) {
    const hh = String(d.getHours()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
  const hh12 = d.getHours() % 12 || 12;
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  return `${hh12}:${mm}:${ss} ${ampm}`;
}

// Self-contained ticking clock — owns its own state so the parent
// component doesn't re-render every second along with it.
function WallClock({ format }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tid = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tid);
  }, []);
  return (
    <Typography
      data-testid="wall-clock"
      variant="h3"
      fontWeight={700}
      sx={{ fontFamily: "monospace", letterSpacing: 3 }}
    >
      {formatClock(now, format)}
    </Typography>
  );
}

// #1558 — the next-class/next-ride block just below WallClock formats
// `nextClass.scheduledTime` / `nextRide.scheduledTime`, the SAME
// FVD-reconciled genuine-UTC field ClassList/RideList/SessionHeader render
// (#805) — a real show/ride time, not a device-local instant like
// WallClock's `now` above. So, unlike formatClock, this one DOES need the
// venue-zone conversion: it goes through the shared formatVenueTime
// (util/classDisplay.js), with `hour12` wired to this screen's stored 12h/24h
// preference (#710) so the toggle above still works. Callers fall back to the
// literal em dash "—" themselves — formatVenueTime returns "" for a
// missing/invalid iso, this screen's own placeholder for "unknown".
export default function InitialSetup() {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { selectedShow, startSession, isSessionReady, pickerUnlocked, unlockPicker, online } =
    useScoreContext();
  // Active org drives which org's shows score-api returns (X-Org-Id header).
  // Single-org scribes are auto-selected by ActiveOrgProvider; super/multi-org
  // users pick it here before the show (card #479).
  const { orgs, activeOrgId, setActiveOrgId } = useActiveOrg();
  const { data: shows, loading, error: showsError } = useShows();
  const { user, removeUser } = useAuth();
  const sub = user?.profile?.sub ?? null;

  // #524/#627 — a scribe assigned within exactly one show skips the picker
  // entirely: the session is just that show. Only relevant on a cold start (no
  // persisted session); once a show is set, this screen isn't reachable except
  // through the lock — and an admin who just unlocked it must not be auto-skipped
  // straight back out (#628).
  const autoSkipEligible = !isSessionReady && !pickerUnlocked;
  const { assignments, loading: loadingAssignments } =
    useScribeAssignments(autoSkipEligible ? shows : null);
  const sole = autoSkipEligible ? soleShow(assignments) : null;
  // Keep the picker hidden while we're still deciding, or when we're about to
  // auto-skip — so the picker never flashes before the redirect.
  const resolvingAutoSkip =
    autoSkipEligible && (loadingAssignments || assignments === null || Boolean(sole));

  useEffect(() => {
    if (!sole) return;
    startSession(sole);
    navigate("/classes", { replace: true });
  }, [sole, startSession, navigate]);

  const [showId, setShowId] = useState("");

  // #2222 — scribe's own picker (a react-select fed by useShows(), NOT
  // ActiveShowContext/ShowSwitcher — this screen is scribe's real show
  // picker, see the file-level note above the component) lists every show
  // the org has ever ingested. Filter to the active date window
  // (isShowInActiveWindow, shared with common-fe's ShowSwitcher), with the
  // same "Show all past shows" escape hatch so an old show stays reachable,
  // never gone. Purely client-side over the already-fetched/cached `shows`
  // list — no extra fetch, no change to useShows()/score-api.
  const [showAllShows, setShowAllShows] = useState(false);
  const visibleShows = useMemo(() => {
    const all = shows ?? [];
    if (showAllShows) return all;
    return all.filter((s) => isShowInActiveWindow(s));
  }, [shows, showAllShows]);

  const handleOrgChange = (e) => {
    setActiveOrgId(e.target.value);
    // Shows are org-scoped — a stale selection from the previous org is invalid.
    setShowId("");
  };
  // R1-UI-003 / #628 — the org + show pickers are behind the PIN lock. A
  // first-time visitor (no session yet) has nothing to protect, so they're open;
  // after that, only a PIN unlock reopens them.
  const selectorsUnlocked = !isSessionReady || pickerUnlocked;

  // #2122 — CARD APPROACH (read before touching this chain again). The card
  // named five hook calls as a serial chain: useShows ->
  // useScribeAssignments(shows) -> useClassesForShow(selectedShow.id) ->
  // useRidesByClassId(assignedClassIds) -> useRidesForClass(nextClass.id),
  // each apparently waiting on the prior's resolved data. Reading each hook
  // (not measuring) split that into what's a GENUINE data dependency and
  // what was an artificial hop this component's own wiring introduced. Two
  // of the claims below were checked by actually running the Cypress specs
  // in cypress/e2e/setup-next-class-prefetch.cy.js and mutating the wiring
  // each one exercises, not just by reading — the GENUINE and TESTED
  // CONCURRENT sections have a surviving spec behind them. The other two
  // ATTEMPTED-AND-REVERTED notes below don't: they record two different
  // production changes this card tried, tested as far as a Cypress network
  // assertion could, and then backed out once that testing turned out
  // unable to establish what it needed to.
  //
  //   GENUINE (kept sequential, can't be removed without an API change):
  //     shows -> assignments: useScribeAssignments calls score-api's
  //     POST /class/batch with the show ids (apps/score-api/src/handlers/
  //     class.js listClassesForShows) — classes are only queryable by id or
  //     by the classesByShow GSI (showId), there is no "mine, org-wide"
  //     read path, so the show ids must exist first. Adding one is a
  //     score-api change, out of scope here (apps/scribe-fe only, and
  //     score-api is a sibling card's active file, #1978).
  //     assignments -> classes: needs a concrete showId.
  //     classes -> ridesByClassId: needs concrete classIds.
  //
  //   ATTEMPTED-AND-REVERTED #1, ridesByClassId -> nextClass ->
  //   useRidesForClass(nextClass.id): an earlier version of this comment
  //   claimed this pair was already-concurrent on the strength of #856's
  //   "no entry yet == not complete" default, and shipped a redundant
  //   "prefetch" hook call to force it. Testing that claim directly (delay
  //   one assigned class's rides heavily, assert the other's ride request
  //   still lands fast) never went red no matter how that pair was wired,
  //   including with the prefetch hook removed entirely — which led to the
  //   discovery in FOUND BUT NOT FIXED HERE below instead of a real proof,
  //   so that claim and the prefetch hook were both dropped.
  //
  //   ATTEMPTED-AND-REVERTED #2, assignments/sole -> classes: this card
  //   tried keying useClassesForShow off `sole?.id ?? selectedShow?.id`
  //   instead of `selectedShow?.id` alone, on the theory that on the
  //   auto-skip path `selectedShow` only updates a render AFTER `sole`
  //   resolves (it flows through startSession -> ScoreContext), and since
  //   that same effect calls navigate() in the same tick, the classes
  //   fetch could lose the race entirely. Measured: reverting to
  //   `selectedShow?.id` alone and re-running the auto-skip spec, the
  //   classes-for-show request still fired. NOT established: why. This
  //   component isn't the only caller of useClassesForShow(selectedShow.id)
  //   — ClassList.jsx:56 calls it too, and ClassList is exactly where the
  //   auto-skip navigate() lands; useCachedResource
  //   (packages/common-fe/src/utils/useCachedResource.js) has no in-flight
  //   dedupe or shared cache-key registry, so a Cypress network assertion
  //   can't attribute an observed request to InitialSetup rather than
  //   ClassList. The `sole?.id` change was therefore reverted: an
  //   unjustified line in this file is worse than no line, and the
  //   measured behavior (a classes-for-show request lands either way) is
  //   equally consistent with "ClassList's own fetch was doing the work
  //   all along." Kept as history here rather than silently dropped, since
  //   the same underlying gap (useCachedResource's lack of dedupe) is one
  //   root cause behind #2126.
  //
  //   TESTED CONCURRENT, not actually a chained hop: useShows is declared
  //   FIRST and reads as a prerequisite for everything below it, but for a
  //   scribe who already has a session, useClassesForShow only needs
  //   `selectedShow.id` — already available from the persisted session on
  //   the very first render (ScoreContext.jsx's useState initializer reads
  //   localStorage synchronously) — and never waits on the shows list at
  //   all. Proven (and mutation-tested: gating the classes call on `shows`
  //   being loaded turns it red) in
  //   cypress/e2e/setup-next-class-prefetch.cy.js's first spec.
  //
  //   FOUND BUT NOT FIXED HERE: while building the (later-abandoned) proof
  //   above for the ridesByClassId/nextClass pair, EVERY cache-first hook in
  //   this app was observed firing its network fetch TWICE on a cold mount,
  //   independent of any of this card's changes. Root cause traced to
  //   useApiConfig (packages/common-fe/src/utils/useApiConfig.js): its
  //   useCallback depends on `activeOrgId`, which resolves asynchronously
  //   after the org-list call — so useScoreApi's `api` object gets a new
  //   identity once org resolves, and since `api` sits in every
  //   useCachedResource hook's dependency array, that single identity change
  //   re-fires every hook's fetch a second time. That doubles first-load
  //   network traffic on every screen, not just this one, which is squarely
  //   the kind of thing #979 (the slow-first-load card this one was split
  //   from) is about — but it lives in packages/common-fe, this card's
  //   sibling #2120's territory (BLOCKED awaiting a decision from Jaybill),
  //   so it's out of scope to fix here. Carded separately as #2126 rather
  //   than left only in this comment.
  //
  //   REJECTED:
  //     - Prefetching classes for the scribe's TENTATIVE (not yet confirmed
  //       by clicking "Start Scoring") dropdown pick on the manual picker.
  //       Rejected: the same assignedClasses/nextClass state also drives the
  //       always-visible "Next assigned class" preview above the picker, so
  //       warming it from an unconfirmed pick could flash a different show's
  //       next class while the scribe is still choosing — an ambiguous
  //       state, not a faster one.
  //     - An org-wide "my classes" score-api endpoint that would remove the
  //       shows -> assignments hop outright. Real option, but it's a
  //       score-api change and this card's scope is apps/scribe-fe only.
  //
  // #647 — the standby screen's "next assigned class": useClassesForShow
  // already filters to this scribe's assignments and sorts by scheduledTime
  // (#627/#632), so the first one that isn't already done IS the next class.
  // showId is only real once a session exists; useClassesForShow(null) is a
  // no-op (no request), so this is safe before a show is picked. Keyed on
  // `selectedShow?.id` (context), NOT the locally-known `sole` — see
  // ATTEMPTED-AND-REVERTED #2 above for why routing `sole` in here directly
  // was tried and backed out.
  const { data: assignedClasses } = useClassesForShow(selectedShow?.id ?? null);
  // #856 — "done" is judged by isClassComplete on the class's OWN rides
  // (Class.status is dead, never written), so nextAssignedClass needs every
  // assigned class's rides to be SURE, but treats a class it hasn't heard
  // back about yet as "not complete" rather than blocking
  // (isClassComplete(undefined) === false). That means nextClass below
  // resolves off assignedClasses ALONE, without needing ridesByClassId to
  // have answered anything yet, so useRidesForClass(nextClass?.id) further
  // down fires in the same render as useRidesByClassId rather than after
  // it — not a sixth serial hop despite reading as one top to bottom.
  const assignedClassIds = useMemo(
    () => (assignedClasses ?? []).map((c) => c.id),
    [assignedClasses],
  );
  const { data: ridesByClassId } = useRidesByClassId(assignedClassIds);
  const nextClass = useMemo(
    () => nextAssignedClass(assignedClasses ?? [], ridesByClassId ?? {}),
    [assignedClasses, ridesByClassId],
  );
  // #647 — same seat-resolution rule as ClassList (scribeAssignments), not a
  // second one. Normally a single position; joined for the rare multi-seat case.
  const nextClassPosition = useMemo(
    () => scribePositionsFor(nextClass, sub).join(" / "),
    [nextClass, sub],
  );
  // #647 — the next ride within that class, off the same source of truth
  // #636 established (useRidesForClass + classDisplay's nextUpcomingRide).
  const { data: nextClassRides } = useRidesForClass(nextClass?.id ?? null);
  const nextRide = useMemo(
    () => nextUpcomingRide(nextClassRides?.rides ?? []),
    [nextClassRides],
  );

  // R1-#144 — resume destination: the route the scribe was on when
  // inactivity kicked in, falling back to their assigned class.
  const resumeDest = useMemo(() => {
    try {
      const stashed = sessionStorage.getItem(RESUME_ROUTE_KEY);
      if (stashed) return stashed;
    } catch {
      // sessionStorage unavailable — fall through to assigned-class fallback.
    }
    if (nextClass) return `/classes/${nextClass.id}`;
    return "/classes";
  }, [nextClass]);

  const handleResume = () => {
    try {
      sessionStorage.removeItem(RESUME_ROUTE_KEY);
    } catch {
      // non-fatal
    }
    navigate(resumeDest, { replace: true });
  };

  // #710 — 12hr/24hr display preference, seeded from localStorage so it
  // survives a reload and carries to the next show; the toggle below writes
  // it back.
  const [timeFormat, setTimeFormatState] = useState(getTimeFormat);
  const handleTimeFormatChange = (e, next) => {
    if (next === null) return;
    setTimeFormatState(next);
    setTimeFormat(next);
  };

  const canStart = Boolean(showId);

  const handleStart = () => {
    const show = (shows ?? []).find((s) => s.id === showId);
    if (!show) return;
    startSession(show);
    navigate("/classes", { replace: true });
  };

  // #524 — while resolving assignments (or about to auto-skip a single-
  // assignment scribe), show a loader rather than flashing the picker.
  // #515 — this is the one true full-viewport route-level bootstrap loader
  // in scribe-fe (no AppShell chrome exists yet); reuse the shared
  // common-fe FullScreenLoader (#514) instead of a hand-rolled Box, same
  // dark-mode-safe color it already carries.
  if (resolvingAutoSkip) {
    return <FullScreenLoader />;
  }

  // #881 — /setup is the one screen a scribe reaches on a stale/wrong active
  // org (kiosk lockdown, #734, otherwise has no escape hatches at all) before
  // a scoring session exists, so it's the closest thing this app has to
  // scoreadmin/announcer's entitlement/role gate. It has an org picker
  // already (below, when unlocked) but never had a visible sign-out — GateHeader
  // adds that plus the active org name, without touching the PIN-gated
  // show-change flow (#628) once a session exists.
  return (
    <>
      <GateHeader onLogout={() => fullSignOut(removeUser)} brandLogo={<GateBrandMark />} />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          pt: "64px",
          bgcolor: "background.default",
        }}
      >
      <Card elevation={4} sx={{ width: "100%", maxWidth: 560, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Logo style={{ width: 160, height: "auto" }} />
          </Box>
          {/* #145/#628 — lock self-positions fixed bottom-right. /setup is
              outside AppShell, so it mounts its own. #629 — same per-show
              PIN as AppShell's lock (falls back to the previously selected
              show, which is still the one in context here). */}
          <PinLock
            pin={getScribePin(selectedShow)}
            onUnlock={unlockPicker}
            testId="scribe-lock"
          />
          {/* #1902 — same reason as the PinLock right above: AppShell's own
              copy (mounted alongside its PinLock) never renders here since
              /setup has no AppShell chrome, and /setup is exactly where a
              scribe lands right after unlocking. */}
          <RebuildLocalDataButton />

          {/* R1-UI-003 — informational standby block. */}
          <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <WallClock format={timeFormat} />
            {/* #710 — 12hr/24hr toggle. Selected state forced to brand
                orange/darkRed in BOTH themes, matching score-fe's
                SheetsPage toggle: MUI's default selected tint is dimmer
                than the unselected label on a dark background, which
                reads as the inactive state. */}
            <ToggleButtonGroup
              value={timeFormat}
              exclusive
              onChange={handleTimeFormatChange}
              size="small"
              aria-label={t("setup.timeFormatAria")}
              data-testid="time-format-toggle"
              sx={{
                "& .MuiToggleButton-root": {
                  color: "text.secondary",
                  borderColor: "divider",
                  px: 1.5,
                  py: 0.25,
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  bgcolor: colors.foxOrange,
                  color: colors.darkRed,
                  fontWeight: 700,
                  "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
                },
              }}
            >
              <ToggleButton value={HOUR_12} data-cy="time-format-12h">
                {t("setup.timeFormat12h")}
              </ToggleButton>
              <ToggleButton value={HOUR_24} data-cy="time-format-24h">
                {t("setup.timeFormat24h")}
              </ToggleButton>
            </ToggleButtonGroup>
            <Divider flexItem />
            <Stack
              data-testid="next-class-block"
              alignItems="center"
              spacing={0.5}
            >
              <Typography variant="caption" color="text.secondary">
                Next assigned class
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {nextClass ? classNumberLabel(nextClass) : "(none scheduled)"}
              </Typography>
              {nextClass && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ color: "text.secondary", fontSize: 14 }}
                >
                  <span>
                    {formatVenueTime(nextClass.scheduledTime, { hour12: timeFormat !== HOUR_24 }) ||
                      "—"}
                  </span>
                  <span>·</span>
                  <span>{nextClass.ring ?? "Ring —"}</span>
                  {nextClassPosition && (
                    <>
                      <span>·</span>
                      <span>Position {nextClassPosition}</span>
                    </>
                  )}
                </Stack>
              )}
              {/* #647 — the next RIDE within that class (time + CNO + rider),
                  same single source of truth #636 established. */}
              {nextClass && nextRide && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {`${
                    formatVenueTime(nextRide.scheduledTime, { hour12: timeFormat !== HOUR_24 }) ||
                    "—"
                  } · ${t(
                    "rides.ridePrefix",
                    { number: rideCnoNumber(nextRide) },
                  )}${
                    nextRide.riderName && nextRide.riderName !== "—"
                      ? ` · ${nextRide.riderName}`
                      : ""
                  }`}
                </Typography>
              )}
            </Stack>
          </Stack>

          {/* R1-#144 — everyday resume gesture. Hidden until a session
              is established (cold-start sign-in still goes through the
              selectors below). */}
          {isSessionReady && (
            <Box sx={{ mb: selectorsUnlocked ? 3 : 0 }}>
              <ScribeResume pin={getScribePin(selectedShow)} onResume={handleResume} />
            </Box>
          )}

          {/* R1-UI-003 — selectors stay collapsed unless unlocked. */}
          {selectorsUnlocked ? (
            <Stack spacing={3} sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("setup.title")}
              </Typography>
              {orgs.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel>{t("setup.orgLabel")}</InputLabel>
                  <Select
                    value={activeOrgId ?? ""}
                    label={t("setup.orgLabel")}
                    onChange={handleOrgChange}
                    disabled={loading}
                  >
                    {orgs.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth>
                <InputLabel>{t("setup.showLabel")}</InputLabel>
                <Select
                  value={showId}
                  label={t("setup.showLabel")}
                  onChange={(e) => setShowId(e.target.value)}
                  disabled={loading}
                >
                  {visibleShows.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.dateRange ? ` — ${s.dateRange}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* #2222 — reachability escape hatch: the show FormControl
                  above only lists shows in the active date window, so this
                  stays visible off the RAW `shows` list (never
                  `visibleShows`) whenever the org has any shows at all —
                  otherwise there would be no way to reach the toggle that
                  reveals an out-of-window one. */}
              {(shows ?? []).length > 0 && (
                <FormControlLabel
                  data-testid="setup-show-all-toggle"
                  control={
                    <Switch
                      size="small"
                      checked={showAllShows}
                      onChange={(e) => setShowAllShows(e.target.checked)}
                      inputProps={{ "aria-label": t("setup.showAllToggle") }}
                    />
                  }
                  label={t("setup.showAllToggle")}
                  sx={{ mt: -1, "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
                />
              )}

              {/* #1737 — an org-scoped local cache miss (a device that hasn't
                  refreshed since #1736 shipped, or a genuinely show-less org)
                  plus either forced-offline or a failed fetchRemote (a real
                  no-signal venue) used to render this as a silent empty
                  dropdown with no explanation and no recovery hint —
                  useShows' `error` was resolved but never even read here.
                  `data-testid` lets Cypress assert on it directly rather than
                  the absence of options. Treat "couldn't reach the network"
                  and "did reach it but the call failed" the same way: both
                  mean the empty list isn't necessarily the truth, unlike a
                  genuinely show-less org. */}
              {!loading && (shows ?? []).length === 0 && (
                <Typography
                  color="text.secondary"
                  data-testid="setup-no-shows"
                >
                  {online && !showsError ? t("setup.noShows") : t("setup.noShowsOffline")}
                </Typography>
              )}

              {/* #515 — MD3 convention (#514): native MUI 7 `loading` prop
                  instead of the manual startIcon + ternary pattern. `loading`
                  here is the shows list still loading (not a submit-in-flight
                  flag — handleStart is synchronous), same reasoning as the
                  Select's disabled={loading} just above. */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                loading={loading}
                disabled={!canStart}
                onClick={handleStart}
                sx={{ py: 1.5, fontWeight: 600 }}
              >
                {t("setup.start")}
              </Button>

              {/* #734 — kiosk lockdown: Account settings moved behind the
                  PIN lock. Only reachable here, inside the unlocked admin
                  screen, never from a free-standing menu. */}
              <Divider />
              <Button
                variant="text"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  window.location.href =
                    import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
                }}
              >
                Account settings
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
      </Box>
    </>
  );
}
